import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { Panel, Popover, Whisper, Tag, FlexboxGrid, List, IconButton, ButtonGroup, Input, Loader, Modal } from 'rsuite'
import ArrowLeftLine from '@rsuite/icons/ArrowLeftLine'
import ArrowRightLine from '@rsuite/icons/ArrowRightLine'
import { useQuery } from '@apollo/client'
import { GQL_QUERY_ALL_SLOTS, FetchAllSlots, FetchAllSlotsVariables } from '@@partner:graphql/query/shop/allSlots.gql'
import { GQL_QUERY_DELIVERY_ZONES, AllDeliveryZonesQuery, AllDeliveryZonesVariables } from '@@partner:graphql/query/deliveryZone/allDeliveryZones.gql'
import { makeBetweenFilter } from '@@partner:helpers/crud/filters'
import { formatDate } from '@@partner:helpers/util'
import { currentBrandVar } from '@@partner:graphql/initial-data'
import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import type { DeliverySlot, DeliveryZone } from '@@partner:types:app'
import SlotBulkUpdatePanel from './SlotBulkUpdatePanel'
import ShopSlotForm from '@components/shop/slot/ShopSlotForm'

import './DeliverySlotsCalendar.scss'

const ZONES_PAGE_SIZE = 20

interface DeliverySlotsCalendarProps {
  onSlotClick?: (slot: DeliverySlot) => void
  daysCount?: number // Количество дней для отображения (по умолчанию 7)
}

// Названия дней недели
const DAY_NAMES = [ 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс' ]
const MONTH_NAMES = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
]

// Получить начало сегодняшнего дня
const getToday = (): Date => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

// Хелпер для цвета заполненности
const getColor = (percent: number): 'red' | 'orange' | 'green' => {
  if (percent >= 90) return 'red'
  if (percent >= 70) return 'orange'
  return 'green'
}

// Генерация цвета для зоны
const getZoneColor = (zoneId: string | number): string => {
  const colors = [
    '#1976d2', '#d32f2f', '#388e3c', '#f57c00', 
    '#7b1fa2', '#5d4037', '#c2185b', '#00796b',
    '#303f9f', '#689f38', '#e64a19', '#455a64'
  ]
  const index = (typeof zoneId === 'string' ? parseInt(zoneId, 10) : zoneId) % colors.length
  return colors[index]
}

// Форматирование даты для заголовка
const formatDateHeader = (date: Date): string => {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`
}

export default function DeliverySlotsCalendar ({ onSlotClick, daysCount = 7 }: DeliverySlotsCalendarProps) {
  const currentBrand = currentBrandVar()
  const currentBrandId = currentBrand?.id

  // Начало периода (сегодня)
  const [ startDate, setStartDate ] = useState(() => getToday())
  const [ selectedZoneId, setSelectedZoneId ] = useState<GQL_ID | null>(null)
  const [ selectedSlots, setSelectedSlots ] = useState<GQL_ID[]>([])
  const [ editSlotId, setEditSlotId ] = useState<GQL_ID | null>(null)
  const [ zoneSearch, setZoneSearch ] = useState('')
  const [ zonesPage, setZonesPage ] = useState(1)
  const [ allZones, setAllZones ] = useState<DeliveryZone[]>([])
  const [ hasMoreZones, setHasMoreZones ] = useState(true)
  const zonesListRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Генерируем массив дней для отображения
  const days = useMemo(() => {
    const result: Date[] = []
    for (let i = 0; i < daysCount; i++) {
      const day = new Date(startDate)
      day.setDate(startDate.getDate() + i)
      result.push(day)
    }
    return result
  }, [ startDate, daysCount ])

  // Конец периода
  const endDate = useMemo(() => {
    const end = new Date(startDate)
    end.setDate(startDate.getDate() + daysCount - 1)
    end.setHours(23, 59, 59, 999)
    return end
  }, [ startDate, daysCount ])

  // Фильтр для зон доставки
  const zonesFilter = useMemo<NestedFilter | undefined>(() => {
    if (!currentBrandId) return undefined
    const filters: NestedFilter[] = [
      { fields: [ 'brand_id' ], op: 'eq', value: currentBrandId }
    ]
    if (zoneSearch.trim()) {
      filters.push({ fields: [ 'name' ], op: 'contains', value: [ zoneSearch.trim() ] })
    }
    return filters.length === 1 ? filters[0] : { and: filters }
  }, [ currentBrandId, zoneSearch ])

  // Загрузка зон доставки с пагинацией
  const { data: zonesData, loading: loadingZones } = useQuery<AllDeliveryZonesQuery, AllDeliveryZonesVariables>(
    GQL_QUERY_DELIVERY_ZONES,
    {
      variables: {
        filter: zonesFilter,
        pager: { page: zonesPage, limit: ZONES_PAGE_SIZE }
      },
      skip: !currentBrandId,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const newZones = data?.allDeliveryZones?.edges ?? []
        const pageInfo = data?.allDeliveryZones?.pageInfo
        setAllZones(prev => zonesPage === 1 ? newZones : [ ...prev, ...newZones ])
        setHasMoreZones(pageInfo ? pageInfo.page < pageInfo.lastPage : false)
      }
    }
  )

  // Сброс при изменении поиска
  const handleZoneSearch = useCallback((value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setZoneSearch(value)
      setZonesPage(1)
      setAllZones([])
      setHasMoreZones(true)
    }, 300)
  }, [])

  // Infinite scroll для зон
  const handleZonesScroll = useCallback(() => {
    const el = zonesListRef.current
    if (!el || loadingZones || !hasMoreZones) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setZonesPage(prev => prev + 1)
    }
  }, [ loadingZones, hasMoreZones ])

  useEffect(() => {
    const el = zonesListRef.current
    if (!el) return
    el.addEventListener('scroll', handleZonesScroll)
    return () => el.removeEventListener('scroll', handleZonesScroll)
  }, [ handleZonesScroll ])

  // Создаем фильтр для слотов
  const slotsFilter = useMemo<NestedFilter>(() => {
    const filters: NestedFilter[] = [
      // Только зональные слоты (без shop_id)
      { fields: [ 'shop_id' ], op: 'is_null' },
      // Диапазон дат
      makeBetweenFilter('ts_start', {
        from: startDate.getTime(),
        to: endDate.getTime()
      }),
      // Выбранная зона (обязательно)
      {
        fields: [ 'zone_id' ],
        op: 'eq',
        value: [ selectedZoneId! ]
      }
    ]

    return { and: filters }
  }, [ startDate, endDate, selectedZoneId ])

  // Загрузка слотов — только при выбранной зоне
  const { data: slotsData, loading: loadingSlots, refetch: refetchSlots } = useQuery<FetchAllSlots, FetchAllSlotsVariables>(
    GQL_QUERY_ALL_SLOTS,
    {
      variables: {
        filter: slotsFilter,
        pager: { page: 1, limit: 1000 },
        sort: { fieldName: 'ts_start', direction: 1 }
      },
      skip: !selectedZoneId,
      fetchPolicy: 'cache-first'
    }
  )

  const slots = useMemo(() => slotsData?.allSlots?.edges ?? [], [ slotsData ])

  // Группируем слоты по датам (с сортировкой по ts_start внутри дня)
  const slotsByDate = useMemo(() => {
    const groups: Record<string, DeliverySlot[]> = {}
    
    slots.forEach(slot => {
      if (slot.period?.from) {
        const date = new Date(parseInt(slot.period.from))
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const dateKey = `${year}-${month}-${day}`
        
        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(slot)
      }
    })

    // Сортируем слоты внутри каждого дня по ts_start
    Object.values(groups).forEach(daySlots => {
      daySlots.sort((a, b) => parseInt(a.period.from) - parseInt(b.period.from))
    })
    
    return groups
  }, [ slots ])

  // Навигация
  const goToPreviousPeriod = useCallback(() => {
    setStartDate(prev => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() - daysCount)
      return newDate
    })
  }, [ daysCount ])

  const goToNextPeriod = useCallback(() => {
    setStartDate(prev => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() + daysCount)
      return newDate
    })
  }, [ daysCount ])

  const goToToday = useCallback(() => {
    setStartDate(getToday())
  }, [])

  // Получить слоты для конкретной даты
  const getSlotsForDate = useCallback((date: Date): DeliverySlot[] => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const dateKey = `${year}-${month}-${day}`
    return slotsByDate[dateKey] || []
  }, [ slotsByDate ])

  // Проверка, является ли дата сегодняшней
  const isToday = useCallback((date: Date): boolean => {
    const today = new Date()
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear()
  }, [])

  // Рендер слота
  const renderSlot = (slot: DeliverySlot) => {
    const ordersPercent = slot.orders_capacity > 0 
      ? Math.round((slot.orders_count / slot.orders_capacity) * 100) 
      : 0
    const zoneName = slot.deliveryZone?.name ?? 'Зона'
    const zoneColor = slot.deliveryZone ? getZoneColor(slot.deliveryZone.id) : '#666'
    
    const slotTooltip = (
      <div>
        <div><strong>{zoneName}</strong></div>
        <div>Время: {slot.slotTime}</div>
        <div>Заказы: {slot.orders_count}/{slot.orders_capacity} ({ordersPercent}%)</div>
        <div>Позиции: {slot.positions_count}/{slot.position_capacity}</div>
        <div>Доступен до: {formatDate(new Date(parseInt(slot.availableUntil)))}</div>
      </div>
    )

    return (
      <Whisper
        key={slot.id}
        trigger="hover"
        speaker={<Popover>{slotTooltip}</Popover>}
      >
        <div
          className={`slot-badge ${selectedSlots.includes(slot.id) ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            if (onSlotClick) {
              onSlotClick(slot)
            }
            else {
              setEditSlotId(slot.id)
            }
          }}
          style={{ borderLeftColor: zoneColor }}
        >
          <input
            type="checkbox"
            checked={selectedSlots.includes(slot.id)}
            onChange={(e) => {
              e.stopPropagation()
              handleToggleSlot(slot.id)
            }}
            onClick={(e) => e.stopPropagation()}
            className="slot-checkbox"
          />
          <span className="slot-time">{slot.slotTime}</span>
          <Tag size="sm" color={getColor(ordersPercent)}>
            {ordersPercent}%
          </Tag>
        </div>
      </Whisper>
    )
  }

  const handleZoneSelect = useCallback((zoneId: GQL_ID | null) => {
    setSelectedZoneId(zoneId)
    setSelectedSlots([])
  }, [])

  const handleToggleSlot = useCallback((slotId: GQL_ID) => {
    setSelectedSlots(prev =>
      prev.includes(slotId)
        ? prev.filter(id => id !== slotId)
        : [ ...prev, slotId ]
    )
  }, [])

  const handleBulkUpdateSuccess = useCallback(() => {
    setSelectedSlots([])
    refetchSlots()
  }, [ refetchSlots ])

  const handleEditSuccess = useCallback(() => {
    setEditSlotId(null)
    refetchSlots()
  }, [ refetchSlots ])

  // Форматирование заголовка периода
  const periodHeader = useMemo(() => {
    const lastDay = days[days.length - 1]
    if (startDate.getMonth() === lastDay.getMonth()) {
      return `${startDate.getDate()} – ${lastDay.getDate()} ${MONTH_NAMES[startDate.getMonth()]} ${startDate.getFullYear()}`
    }
    return `${formatDateHeader(startDate)} – ${formatDateHeader(lastDay)} ${lastDay.getFullYear()}`
  }, [ startDate, days ])

  return (
    <div className="DeliverySlotsCalendar">
      <FlexboxGrid>
        {/* Левая панель - список зон */}
        <FlexboxGrid.Item colspan={6}>
          <Panel 
            header="Зоны доставки" 
            bordered 
            className="zones-panel"
          >
            <div className="zones-search">
              <Input
                placeholder="Поиск зоны..."
                onChange={handleZoneSearch}
                size="sm"
              />
            </div>
            <div className="zones-list-scroll" ref={zonesListRef}>
              <List hover>
                {allZones.map((zone: DeliveryZone) => (
                  <List.Item
                    key={zone.id}
                    className={`zone-item ${selectedZoneId === zone.id ? 'active' : ''}`}
                    onClick={() => handleZoneSelect(zone.id)}
                  >
                    <div 
                      className="zone-color-indicator" 
                      style={{ backgroundColor: getZoneColor(zone.id) }}
                    />
                    <div className="zone-info">
                      <div className="zone-name">{zone.name}</div>
                      <div className="zone-shop">{zone.shop?.name ?? 'Без магазина'}</div>
                    </div>
                  </List.Item>
                ))}
              </List>
              {loadingZones && <div className="loading-text"><Loader content="Загрузка зон..." /></div>}
              {!loadingZones && allZones.length === 0 && (
                <div className="loading-text">Зоны не найдены</div>
              )}
            </div>
          </Panel>
        </FlexboxGrid.Item>

        {/* Правая панель - недельный календарь */}
        <FlexboxGrid.Item colspan={18}>
          {!selectedZoneId ? (
            <Panel bordered className="calendar-panel">
              <div className="calendar-placeholder">
                <div className="calendar-placeholder-icon">📅</div>
                <div className="calendar-placeholder-text">Выберите зону доставки для просмотра календаря слотов</div>
              </div>
            </Panel>
          ) : (
            <Panel bordered className="calendar-panel">
              {/* Панель массового обновления */}
              <SlotBulkUpdatePanel
                show={selectedSlots.length > 0}
                selectedIds={selectedSlots}
                onSuccess={handleBulkUpdateSuccess}
              />

              {/* Заголовок с навигацией */}
              <div className="week-calendar-header">
                <ButtonGroup>
                  <IconButton
                    icon={<ArrowLeftLine />}
                    onClick={goToPreviousPeriod}
                    appearance="subtle"
                    size="sm"
                  />
                  <IconButton
                    icon={<ArrowRightLine />}
                    onClick={goToNextPeriod}
                    appearance="subtle"
                    size="sm"
                  />
                </ButtonGroup>

                <div className="period-title">
                  <span className="period-text">{periodHeader}</span>
                  {loadingSlots && (
                    <span className="loading-indicator">Загрузка...</span>
                  )}
                </div>

                <button
                  className="today-button"
                  onClick={goToToday}
                >
                  Сегодня
                </button>
              </div>

              {/* Сетка календаря */}
              <div className="week-calendar-grid">
                {/* Заголовки — дни недели + даты */}
                <div className="week-calendar-row week-calendar-row--header">
                  {days.map((day, i) => {
                    const dayOfWeek = (day.getDay() + 6) % 7 // 0=Пн, 6=Вс
                    const isWeekend = dayOfWeek >= 5
                    return (
                      <div key={i} className={`week-calendar-cell week-calendar-cell--header ${isWeekend ? 'weekend' : ''}`}>
                        {DAY_NAMES[dayOfWeek]}, {day.getDate()}
                      </div>
                    )
                  })}
                </div>

                {/* Одна строка с днями */}
                <div className="week-calendar-row">
                  {days.map((day, i) => {
                    const daySlots = getSlotsForDate(day)
                    const isTodayCell = isToday(day)
                    const dayOfWeek = (day.getDay() + 6) % 7
                    const isWeekend = dayOfWeek >= 5

                    return (
                      <div
                        key={i}
                        className={`week-calendar-cell ${isTodayCell ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                      >
                        <div className="day-slots">
                          {daySlots.map(slot => renderSlot(slot))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Легенда */}
              <div className="calendar-legend">
                <div className="legend-item">
                  <Tag size="sm" color="green">0-70%</Tag>
                  <span>Свободно</span>
                </div>
                <div className="legend-item">
                  <Tag size="sm" color="orange">70-90%</Tag>
                  <span>Заполняется</span>
                </div>
                <div className="legend-item">
                  <Tag size="sm" color="red">90%+</Tag>
                  <span>Почти заполнен</span>
                </div>
              </div>
            </Panel>
          )}
        </FlexboxGrid.Item>
      </FlexboxGrid>

      {/* Модалка редактирования слота */}
      <Modal open={!!editSlotId} onClose={() => setEditSlotId(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Редактирование слота</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editSlotId && (
            <ShopSlotForm
              slotId={editSlotId}
              onSuccess={handleEditSuccess}
              onCancel={() => setEditSlotId(null)}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}
